/**
 * Minimal NerdGraph NRQL transport plus a translator that reshapes NerdGraph
 * results into the "chart data" shape the New Relic SDK hands to consumers.
 *
 * The nerdlet source reads results as:
 *   result.data[i].metadata.groups.find((g) => g.name === "storeNo").value
 *   result.data[i].data[j].<column>
 * so the translation below has to reproduce both of those.
 */

// Extension-qualified so this module is importable by plain Node (see tests).
import { getEndpoint, getRuntimeConfig } from "./config.js";

const NRQL_DOCUMENT = `
  query StandaloneNrql($accountId: Int!, $nrql: Nrql!) {
    actor {
      account(id: $accountId) {
        nrql(query: $nrql) {
          results
          metadata {
            eventTypes
            facets
          }
        }
      }
    }
  }
`;

// Mirrors the default New Relic categorical series palette closely enough for
// any consumer that reads metadata.color.
const SERIES_COLORS = [
  "#3ED2F0", "#11A600", "#FFC400", "#A45AC1", "#0189A6",
  "#FF7E1D", "#00B4A0", "#F0466F", "#7DBA00", "#8E6FDB",
];

const colorFor = (index) => SERIES_COLORS[index % SERIES_COLORS.length];

function firstNumericKey(row, skip) {
  return Object.keys(row).find(
    (key) => !skip.has(key) && typeof row[key] === "number"
  );
}

/**
 * @param {Array<object>} results raw NerdGraph `results` rows
 * @param {{facets?: string[]|null, eventTypes?: string[]}} metadata
 * @returns {Array<{metadata: object, data: Array<object>}>}
 */
export function toChartData(results, metadata) {
  if (!Array.isArray(results)) return [];

  const facets = Array.isArray(metadata?.facets) ? metadata.facets : null;

  if (facets && facets.length > 0) {
    const facetKeys = new Set(facets);

    return results.map((row, index) => {
      const groups = facets.map((name) => ({
        type: "facet",
        name,
        value: row[name],
      }));

      Object.keys(row)
        .filter((key) => !facetKeys.has(key))
        .forEach((name) => groups.push({ type: "function", name, value: name }));

      const point = { ...row };
      const measure = firstNumericKey(row, facetKeys);
      if (measure !== undefined) point.y = row[measure];

      return {
        metadata: {
          id: `series-${index}`,
          name: facets.map((f) => String(row[f])).join(", "),
          color: colorFor(index),
          viz: "main",
          groups,
          units_data: {},
        },
        data: [point],
      };
    });
  }

  // Non-faceted query: the SDK returns a single series holding every row.
  const columns = Object.keys(results[0] || {});

  return [
    {
      metadata: {
        id: "series-0",
        name: metadata?.eventTypes?.[0] || "Results",
        color: colorFor(0),
        viz: "main",
        groups: columns.map((name) => ({ type: "function", name, value: name })),
        units_data: {},
      },
      data: results.map((row) => ({ ...row })),
    },
  ];
}

export class NrqlError extends Error {
  constructor(message, details) {
    super(message);
    this.name = "NrqlError";
    this.details = details;
  }
}

/**
 * Executes a single NRQL query against NerdGraph.
 * Resolves to chart data; throws NrqlError on transport or query failure.
 */
/**
 * Issues an arbitrary NerdGraph document through the configured endpoint and
 * returns `data`, throwing NrqlError on transport or GraphQL failure.
 *
 * In proxy mode the backend attaches credentials, so no key is ever sent from
 * the browser. The proxy only forwards read-only NRQL and entity documents.
 */
const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    });
  });

export async function runGraphQL({ query, variables, signal, attempt = 0 }) {
  const { apiKey, authMode } = getRuntimeConfig();
  const endpoint = getEndpoint();

  const headers = { "Content-Type": "application/json" };
  if (apiKey && authMode !== "proxy") headers["API-Key"] = apiKey;

  const response = await fetch(endpoint, {
    method: "POST",
    headers,
    signal,
    body: JSON.stringify({ query, variables: variables || {} }),
  });

  // NerdGraph rate-limits bursts. A dashboard opens ~17 queries at once, so
  // back off and retry rather than surfacing a 429 as a broken widget.
  if (response.status === 429 && attempt < 3) {
    const retryAfter = Number(response.headers.get("retry-after"));
    const waitMs = Number.isFinite(retryAfter) && retryAfter > 0
      ? retryAfter * 1000
      : 400 * 2 ** attempt;
    await sleep(waitMs, signal);
    return runGraphQL({ query, variables, signal, attempt: attempt + 1 });
  }

  if (!response.ok) {
    let detail = "";
    try {
      const body = await response.json();
      detail = body?.errors?.[0]?.message ? ` - ${body.errors[0].message}` : "";
    } catch {
      /* non-JSON error body */
    }
    throw new NrqlError(
      `NerdGraph responded ${response.status} ${response.statusText}${detail}`,
      { status: response.status }
    );
  }

  const payload = await response.json();
  if (payload.errors?.length) {
    throw new NrqlError(payload.errors[0].message, payload.errors);
  }
  return payload.data;
}

export async function runNrql({ accountId, query, signal }) {
  const numericAccountId = Number(accountId);
  if (!Number.isFinite(numericAccountId)) {
    throw new NrqlError(`Invalid accountId: ${accountId}`);
  }
  if (!query) {
    throw new NrqlError("Missing NRQL query");
  }

  const payload = await runGraphQL({
    query: NRQL_DOCUMENT,
    variables: { accountId: numericAccountId, nrql: query },
    signal,
  });

  const nrql = payload?.actor?.account?.nrql;
  if (!nrql) {
    throw new NrqlError(
      `No NRQL result returned for account ${numericAccountId}. Check that the API key can read this account.`
    );
  }

  return {
    data: toChartData(nrql.results, nrql.metadata),
    rawResults: nrql.results || [],
    metadata: nrql.metadata || {},
  };
}
